import React, { useContext, useEffect, useState } from 'react';
import { Tooltip } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { Context } from '../../../App';
import { Link } from 'react-router-dom';
import axios from 'axios';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import { Box } from '@mui/material';
import { PolarEmbedCheckout } from '@polar-sh/checkout/embed';

const SubscriptionBox = ({
  subscription,
  checked,
  isCheckoutInProgress,
  setIsCheckoutInProgress,
  activePlanName,
  setActivePlanName,
  onCheckoutSuccess,
  onCheckoutError,
}) => {
  const { user, isResolved, theme } = useContext(Context);
  const [checkoutInstance, setCheckoutInstance] = useState(null);
  const [checkoutSucceeded, setCheckoutSucceeded] = useState(false);

  useEffect(() => {
    return () => {
      if (checkoutInstance) {
        checkoutInstance.close();
      }
    };
  }, [checkoutInstance]);

  useEffect(() => {
    if (window.__gcCheckoutModalShown) return;
    const didSucceed = sessionStorage.getItem('polarCheckoutSuccess');
    if (didSucceed) {
      window.__gcCheckoutModalShown = true;
      sessionStorage.removeItem('polarCheckoutSuccess');
      onCheckoutSuccess?.();
    }
  }, []);

  const handleSubscription = async (planName) => {
    setIsCheckoutInProgress(true);
    setActivePlanName(planName);
    setCheckoutSucceeded(false);
    try {
      const { data } = await axios({
        'Content-Type': 'application/json',
        method: 'POST',
        url: '/api/v1/bookings/handle-subscription-session',
        data: {
          planName: subscription.name,
          billingPeriod: checked ? 'yearly' : 'monthly',
        },
      });

      if (data.mode === 'portal') {
        window.location.href = data.sessionUrl;
        return;
      }

      const checkout = await PolarEmbedCheckout.create(data.sessionUrl, {
        theme: theme === 'dark' ? 'dark' : 'light',
        onLoaded: () => {
          console.log('Checkout loaded successfully');
        },
      });

      setCheckoutInstance(checkout);

      const handleCheckoutSuccess = () => {
        sessionStorage.setItem('polarCheckoutSuccess', '1');
        setCheckoutSucceeded(true);
        onCheckoutSuccess?.();
        setIsCheckoutInProgress(false);
      };

      checkout.addEventListener('success', handleCheckoutSuccess);
      checkout.addEventListener('confirmed', handleCheckoutSuccess);

      checkout.addEventListener('close', () => {
        setCheckoutInstance(null);
        if (checkoutSucceeded) {
          onCheckoutSuccess?.();
        } else {
          setIsCheckoutInProgress(false);
        }
        setActivePlanName('');
      });
    } catch (err) {
      onCheckoutError?.(err.response?.data?.message || 'Something went wrong. Please try again.');
      setIsCheckoutInProgress(false); // This is inside catch block because it sets to true before page redirect.
      setActivePlanName('');
    }
  };

  const planLevels = ['basic', 'standard', 'premium', 'professional'];

  const subscriberButtonTextHandler = (subscriptionBoxName) => {
    const currentLevel = planLevels.indexOf(user.role);
    const targetLevel = planLevels.indexOf(subscriptionBoxName);

    if (currentLevel === targetLevel) return 'Your current plan!';
    if (currentLevel < targetLevel) return 'Upgrade';
    return 'Downgrade';
  };

  const subscriberButtonDisabledHandler = (subscriptionBoxName) => {
    const currentLevel = planLevels.indexOf(user.role);
    const targetLevel = planLevels.indexOf(subscriptionBoxName);
    if (currentLevel === targetLevel) return true;
  };

  return (
    <div className="box">
      {subscription.name === 'premium' && (
        <Tooltip title={'Best Seller Plan'}>
          <StarIcon color={'primary'} style={{ position: 'absolute', top: 10, right: 10, fontSize: 40 }} />
        </Tooltip>
      )}
      <div className="title">{subscription.name.toUpperCase()}</div>
      <div className="price">
        {subscription.name === 'basic' ? (
          'FREE'
        ) : (
          <>
            {subscription.priceMonthly !== undefined && <span className="currencySymbol">$</span>}
            {checked ? subscription.priceYearly / 100 : subscription.priceMonthly / 100}
            {subscription.priceMonthly !== undefined && <span> {checked ? '/ year' : '/ month'}</span>}
          </>
        )}
      </div>

      <div className="properties">
        {subscription.name === 'basic' && (
          <p>
            <CheckCircleOutlinedIcon color="info" fontSize={'small'} /> Free forever. No credit card required.
          </p>
        )}
        {subscription.name !== 'basic' && (
          <p>
            <CheckCircleOutlinedIcon color="info" fontSize={'small'} /> Cancel anytime.
          </p>
        )}

        <p>
          <Tooltip title="How many calculations you can run per month in this plan.">
            <CheckCircleOutlinedIcon color="success" fontSize={'small'} />
          </Tooltip>{' '}
          Calculation count limit: {subscription.calculationCount === 10000 ? 'Unlimited' : subscription.calculationCount}
        </p>
        <p>
          <Tooltip title="Whether saving calculations is available on this plan.">
            <CheckCircleOutlinedIcon color="success" fontSize={'small'} />
          </Tooltip>{' '}
          Saving calculations: {subscription.saveCount > 0 ? 'Enabled' : 'Disabled'}
        </p>
        <p>
          <Tooltip title="Total number of calculations you can save on this plan.">
            <CheckCircleOutlinedIcon color="success" fontSize={'small'} />
          </Tooltip>{' '}
          Save count limit: {subscription.saveCount === 10000 ? 'Unlimited' : subscription.saveCount}
        </p>
        <p>
          <Tooltip title="Maximum number of projects you can create.">
            <CheckCircleOutlinedIcon color="success" fontSize={'small'} />
          </Tooltip>{' '}
          Project limit: {subscription.projectCountLimit === 10000 ? 'Unlimited' : subscription.projectCountLimit}
        </p>
        <p>
          <Tooltip title="Maximum number of calculations you can attach to a single project.">
            <CheckCircleOutlinedIcon color="success" fontSize={'small'} />
          </Tooltip>{' '}
          Attachment limit per project:{' '}
          {subscription.attachedCalculationCountLimit === 10000 ? 'Unlimited' : subscription.attachedCalculationCountLimit}
        </p>
        <p>
          <Tooltip title="Access to detailed step-by-step calculation breakdowns.">
            <CheckCircleOutlinedIcon color="success" fontSize={'small'} />
          </Tooltip>{' '}
          Calculation details: {subscription.calculationSteps ? 'Enabled' : 'Disabled'}
        </p>
        <p>
          <Tooltip title="Export calculation results to PDF reports.">
            <CheckCircleOutlinedIcon color="success" fontSize={'small'} />
          </Tooltip>{' '}
          PDF Exports: {subscription.pdfExport ? 'Enabled' : 'Disabled'}
        </p>
      </div>
      <div className="price-button">
        {/* 1) If there is no user, show signup buttons */}
        {isResolved && !user && (
          <Link to="/signup">
            <Button
              variant="outlined"
              sx={{
                minWidth: 180, // lock width if needed
                height: 48, // keep button height fixed
              }}
            >
              Sign Up
            </Button>
          </Link>
        )}
        {/* 2) If there is user, show appropriate button texts and handle subscription*/}
        {isResolved && user && (
          <Button
            variant="outlined"
            size="large"
            onClick={() => handleSubscription(subscription.name)}
            disabled={isCheckoutInProgress || subscriberButtonDisabledHandler(subscription.name)}
            sx={{
              minWidth: 180, // lock width if needed
              height: 48, // keep button height fixed
            }}
          >
            {isCheckoutInProgress && subscription.name === activePlanName ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                }}
              >
                <CircularProgress size={24} />
              </Box>
            ) : (
              subscriberButtonTextHandler(subscription.name)
            )}
          </Button>
        )}
      </div>
    </div>
  );
};
export default SubscriptionBox;
